import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '../../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await api.get(`/invites/validate/${token}`);
      setInviteData(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/invites/accept/${token}`, { password });
      
      // Auto-login the user
      if (response.data.token && response.data.user) {
        login(response.data.user, response.data.token);
        toast.success('Account created successfully! Welcome to Players Health.');
        navigate('/admin');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B6FE8]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border border-[#E5E7EB] shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#FEF2F2] rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-[#991B1B]" />
            </div>
            <h2 className="text-xl font-bold text-[#1A1A1A] mb-2">Invalid Invitation</h2>
            <p className="text-[#6B7280] mb-6">{error}</p>
            <Button
              onClick={() => navigate('/admin/login')}
              className="bg-[#7B6FE8] hover:bg-[#6B5CE7] text-white"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border border-[#E5E7EB] shadow-lg">
        <CardHeader className="text-center pb-2">
          <img 
            src="/logo-dark.png" 
            alt="Players Health" 
            className="h-10 mx-auto mb-4"
          />
          <CardTitle className="text-xl font-bold text-[#1A1A1A]">
            Complete Your Registration
          </CardTitle>
          <p className="text-sm text-[#6B7280] mt-2">
            You've been invited to join the Players Health Admin Portal
          </p>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Invite Details */}
          <div className="bg-[#EEF0FF] border border-[#C4BFFF] rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#7B6FE8] rounded-full">
                <Shield className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="font-semibold text-[#1A1A1A]">{inviteData?.name}</p>
                <p className="text-sm text-[#6B7280]">{inviteData?.email}</p>
              </div>
            </div>
            <div className="text-sm text-[#6B5CE7]">
              Role: <span className="font-semibold capitalize">{inviteData?.role?.replace('_', ' ')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-[#1A1A1A] font-medium">
                Create Password *
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-1.5 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                placeholder="Minimum 6 characters"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-[#1A1A1A] font-medium">
                Confirm Password *
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="mt-1.5 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                placeholder="Re-enter your password"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#7B6FE8] hover:bg-[#6B5CE7] text-white h-11"
            >
              {submitting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  Creating Account...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Create Account & Sign In
                </div>
              )}
            </Button>
          </form>

          <p className="text-xs text-center text-[#9CA3AF] mt-6">
            By creating an account, you agree to the Players Health Terms of Service and Privacy Policy.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvite;
